import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  CASE_SORT,
  ORIGINS,
  ORIGIN_LABEL,
  inLogistics,
  matchesSearch,
  shippingBadge,
  type JobOrder,
  type SortPreset,
  caseFinished,
} from '@/domain';
import { useOrdersInHand } from '@/application/usecases/useOrders';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { strings } from '@/shared/strings';
import { CaseTable } from '@/ui/data/CaseTable';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import type { Column } from '@/ui/data/ResponsiveTable';
import { ShippingBadge } from '@/ui/data/ShippingBadge';
import { ShippingDates } from '@/ui/data/ShippingDates';
import { SortSelect } from '@/ui/data/SortSelect';
import { CASE_SORT_LABEL, ORIGIN_SORT_KEYS } from '@/ui/data/casePresets';
import { orderColumns } from '@/ui/data/orderColumns';
import { GoBackIcon } from '@/ui/icons';
import { SkeletonRows } from '@/ui/feedback/Loading';
import styles from './StageScreen.module.css';

/**
 * The Logistics column's own screen. Section 2 of the request of
 * 13 September 2026.
 *
 * A table of ORDERS, not of kits, because a case in logistics is not being
 * worked at a bench: its tray is finished and what has moved is the order. It
 * is the same information the Shipping screen holds, for a manager who does not
 * hold `shipping` and reads the floor rather than the dispatch desk.
 *
 * Asked for WITH the dispatched cases, as the shipping screen asks. Two of the
 * four things the card counts, a case awaiting its waybill and a case on its
 * way, are dispatched orders, and `GET /job-orders` leaves those out by
 * default: without the flag the card would say five and its own screen would
 * list two.
 */
export function LogisticsScreen() {
  const navigate = useNavigate();
  const { data, isPending, isError } = useOrdersInHand();
  const [query, setQuery] = useState('');

  const rows = useMemo(
    () =>
      (data?.items ?? []).filter((order) =>
        inLogistics({
          state: order.state,
          // Every kit Ready is what the order's own state already says once it
          // is out of production; a held order is the only one that hides it.
          kitsAllReady: order.currentStage === null || order.currentStage === 'ready',
          delivered: order.dispatch.deliveredAt !== null,
        }),
      ),
    [data],
  );

  const visible = rows.filter((order) =>
    matchesSearch(query, [
      order.jobOrderNo,
      order.dentistName,
      order.patientName,
      order.dispatch.trackingCode,
    ]),
  );

  const presets = useMemo<SortPreset<JobOrder>[]>(
    () => [
      // Newest first unless the reader chose otherwise. Request of 5 October 2026, section 2.
      { key: 'newestFirst', label: strings.table.newestFirst, compare: CASE_SORT.newestFirst },
      { key: 'dueSoonest', label: strings.table.dueSoonest, compare: CASE_SORT.dueSoonest },
      { key: 'urgentFirst', label: strings.table.urgentFirst, compare: CASE_SORT.urgentFirst },
      { key: 'doctorAtoZ', label: strings.table.doctorAtoZ, compare: CASE_SORT.doctorAtoZ },
      /*
       * One origin's cases first, the Logistics column being the end of the
       * production line. Section 2 of the request of 24 September 2026. Sorted
       * here, as the rest of this list is: it holds every order in logistics at
       * once rather than a page of them.
       */
      ...ORIGIN_SORT_KEYS.map((key) => ({
        key,
        label: CASE_SORT_LABEL[key],
        compare: CASE_SORT[key],
      })),
    ],
    [],
  );
  const order = useTableOrder('logistics', presets);
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('logistics');

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.orders.search,
      placeholder: strings.orders.searchPlaceholder,
      value: query,
      onChange: setQuery,
    },
  ];

  const columns: Column<JobOrder>[] = [
    {
      key: 'dispatchedAt',
      label: strings.line.logisticsColDispatched,
      sortable: true,
      sortValue: (row) => row.dispatch.dispatchedAt ?? '',
      // When it left, when it arrived, and how long it has waited for its code.
      render: (row) => <ShippingDates dispatch={row.dispatch} />,
    },
    {
      // What the origin sorts move, on the row. Section 2 of the request of 24 September 2026.
      key: 'origin',
      label: strings.table.origin,
      sortable: true,
      sortValue: (row) => ORIGINS.indexOf(row.origin),
      render: (row) => ORIGIN_LABEL[row.origin],
    },
    {
      key: 'daysLeft',
      label: strings.orders.due,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.daysLeft,
      render: (row) => <DeliveryDate date={row.deliveryDate} finished={caseFinished(row)} />,
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    ...orderColumns(['carrier', 'tracking', 'createdAt', 'caseType', 'product', 'clinic', 'paidAt']),
  ];

  return (
    <div>
      <button className={styles.back} onClick={() => void navigate('/line')}>
        <GoBackIcon /> {strings.stage.back}
      </button>

      <div className={styles.head}>
        <span className={styles.lbl}>{strings.line.logisticsLabel}</span>
        <h2>{strings.line.logisticsTitle}</h2>
        <span className={styles.hint}>{strings.line.logisticsHint}</span>
      </div>

      {isError && <p className={styles.hint}>{strings.errors.server}</p>}
      {isPending && <SkeletonRows rows={4} label={strings.common.loadingOrders} />}

      {!isPending && !isError && (
        <>
          <FilterBar
            label={strings.orders.filtersLabel}
            filters={filters}
            sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
          />

          <CaseTable
            customize={layout}
            columns={columns}
            rows={order.apply(visible)}
            sort={order.column}
            onSortChange={order.sortByColumn}
            identity={(row) => ({
              jobOrderNo: row.jobOrderNo,
              urgent: row.urgent,
              redo: row.redoOf !== null,
              held: row.hold.held,
              dentistName: row.dentistName,
              patientName: row.patientName,
            })}
            // The same pill the shipping screen shows, so "Shipped" means one
            // thing wherever somebody reads it. Section 7.
            status={(row) => <ShippingBadge order={row} />}
            statusSortValue={(row) => shippingBadge(row).key}
            rowKey={(row) => row.jobOrderNo}
            onRowClick={(row) => void navigate(`/orders/${row.jobOrderNo}`)}
            caption={strings.line.logisticsCaption}
            emptyHeading={strings.line.logisticsEmptyHeading}
            emptyDetail={strings.line.logisticsEmptyDetail}
          />
        </>
      )}
    </div>
  );
}
